/**
 * CaptionStalenessNotice — "captions are behind the translation, refresh them?"
 *
 * Captions bake `TranslationPhrase.translatedValue` at produce time, so they
 * drift when a translation is edited without the track being re-synced. This
 * makes that drift visible and the fix explicit, because a full re-sync also
 * discards caption STRUCTURE edits (synthetic line breaks, merged rows) — so it
 * is the user's call, not something to apply silently.
 *
 * Passive and non-blocking, modelled on `SidebarUpdateCard` (see
 * docs/client-version-check.md — "it does not interrupt editing the way the old
 * popup did"). Renders nothing unless something is actually stale, and the
 * lossy action is confirmed first.
 *
 * Visually it is a persistent contextual banner, not a toast: it describes a
 * standing condition rather than an event, so it borrows the toast's anatomy
 * (neutral surface, one status badge, a primary action, shared close
 * icon — docs/design-system.md "Notification Toasts") but sits in the
 * transcript header flow. It lives in `Transcript`'s `belowHeader` slot, a
 * wrap-capable flex row shared with the highlight nav, and claims that row's
 * full width so the narrow panel wraps the actions under the message rather
 * than squeezing the button label onto two lines.
 *
 * IMPORTANT: this component calls `useTranslatedCaptionStaleness()` itself. Each
 * surface overrides `StoreContext` with its own RootStore proxy, and a component
 * that RENDERS that provider resolves the outer context — so the hook has to run
 * here, inside the provider, for the clip surface to read its own editor. Hosts
 * pass `<CaptionStalenessNotice />` and nothing else.
 */

import { ConfirmModal } from '@src/components/common/ConfirmModal';
import { PARAGRAPH_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import CircleExclammationIcon from '@src/svgs/CircleExclammationIcon';
import CloseIcon from '@src/svgs/CloseIcon';
import ExclamationIcon from '@src/svgs/ExclamationIcon';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { useTranslatedCaptionStaleness } from './hooks/useTranslatedCaptionStaleness';

const TOOLTIP_ID = 'caption-staleness-tooltip';

const EXPLAINER =
  'Captions store their own copy of the translation, made when they were ' +
  'produced. Refreshing replaces that text with the current translation. ' +
  'Caption line breaks and merged rows are rebuilt, so any you added by hand ' +
  'are discarded — you can undo the refresh if you change your mind.';

export const CaptionStalenessNotice: React.FC = observer(() => {
  const { isStale, refresh } = useTranslatedCaptionStaleness();
  const [confirming, setConfirming] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Nothing to say. `isStale` re-derives from observables, so this also hides
  // the notice by itself once a refresh has landed.
  if (!isStale || dismissed) return null;

  return (
    <>
      <Notice role="status" data-testid="caption-staleness-notice">
        {/* Gold = the toast's `warning` badge: it worked, but with a caveat. */}
        <Badge aria-hidden="true">
          <ExclamationIcon strokeColor="var(--background)" />
        </Badge>
        <Message
          data-tooltip-id={TOOLTIP_ID}
          data-tooltip-content={EXPLAINER}
          tabIndex={0}
        >
          Captions were produced before some translation edits.
        </Message>
        <Actions>
          <RefreshButton
            type="button"
            onClick={() => setConfirming(true)}
            data-testid="caption-staleness-refresh"
          >
            Refresh Captions
          </RefreshButton>
          <DismissButton
            type="button"
            onClick={() => setDismissed(true)}
            aria-label="Dismiss"
            data-testid="caption-staleness-dismiss"
          >
            <CloseIcon width={12} height={12} />
          </DismissButton>
        </Actions>
      </Notice>
      {ReactDOM.createPortal(
        <Tooltip
          id={TOOLTIP_ID}
          style={PARAGRAPH_TOOLTIP_STYLE}
          place="bottom"
        />,
        document.body,
      )}
      {confirming && (
        <ConfirmModal
          closeModal={() => setConfirming(false)}
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false);
            refresh();
          }}
          title="Refresh Captions From Translation"
          message="Caption text will be updated to match the current translation. Caption line breaks and merged rows are rebuilt, so any you added by hand will be discarded. You can undo this."
          icon={
            <CircleExclammationIcon
              width="16"
              height="16"
              strokeColor="var(--accent)"
            />
          }
          accentColor="var(--primary)"
          confirmText="Refresh Captions"
          cancelText="Cancel"
        />
      )}
    </>
  );
});

// Neutral surface, as the toast: the badge is the only coloured element, so the
// notice reads at the same volume as the header controls around it instead of
// shouting in gold. `flex-basis: 100%` takes the whole `BelowHeaderRow` line.
const Notice = styled.div`
  flex: 1 1 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--spacing-xs) var(--spacing-sm);
  padding: var(--spacing-xs) var(--spacing-xs) var(--spacing-xs)
    var(--spacing-sm);
  background: var(--surface-elevated);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-button);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  line-height: var(--leading-text-xs);
  color: var(--foreground);
`;

const Badge = styled.span`
  flex: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--accent);
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 10px;
    height: 10px;
  }
`;

// Grows to fill the line. The basis is roughly half the sentence: narrower than
// that and the message would squeeze into a three-line column beside the
// actions, so instead `Actions` wraps below and right-aligns via its auto
// margin, and the message takes the full first line.
const Message = styled.span`
  flex: 1 1 26ch;
  min-width: 0;
  cursor: help;
`;

const Actions = styled.span`
  flex: none;
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-2xs);
`;

// Primary green: this is the row's one action, and the only way out of the
// stale state (docs/design-system.md "Button Hierarchy"). Kept at the row's
// 24px control height rather than the 32px sm button so it sits in the header.
const RefreshButton = styled.button`
  flex: none;
  height: 24px;
  padding: 0 var(--spacing-sm);
  background: var(--primary);
  border: none;
  border-radius: var(--radius-button);
  color: var(--background);
  font-family: inherit;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semi-bold);
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: var(--primary-hover);
  }
`;

const DismissButton = styled.button`
  flex: none;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: var(--text-muted);
  cursor: pointer;
  transition:
    color 0.15s ease,
    background 0.15s ease;

  &:hover {
    color: var(--foreground);
    background: var(--surface-elevated-hover);
  }
`;
